import { AssetTypes, PaymentActionTypes, SelectOption } from '@tassat/common-definitions';
import { AllowedTransactionType } from '@tassat/fred-extended-definitions';
import React from 'react';

import CreatePledgeConfirmation from 'Containers/client/forms/createPledgeConfirmation';
import { getAccount, getTransactionsSync, validateWalletAddressWithCore } from 'Containers/client/forms/helpers/createPledgeFormFn';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAddressBookContacts } from 'Selectors/addressBook';
import { getAllSecurityCurrencyPairs, getEntitledAssetsByTransactionType } from 'Selectors/assets';
import { getSelectedClientAccount } from 'Selectors/common';
import { ActionOverlay } from 'SharedComponents/layouts/overlays';
import WalletCompactDetails from 'SharedComponents/wallets/WalletCompactDetails';

import CreatePledgeForm from './createPledgeForm';
import type { ICreatePledgeProps } from '../createPledge/index';

export function CreatePledge(props: ICreatePledgeProps) {
    const {
        title,
        transactionValues,
        setTransactionValues,
        handleCreatePledge,
        goBack,
        accountPositionBalance
    } = props;
    const currentWallet = useAppSelector(getSelectedClientAccount);
    const addressBookOptions = useAppSelector(getAddressBookContacts);
    const allSecurityCurrencyPairs = useAppSelector(getAllSecurityCurrencyPairs);
    const entitledAssetsByType = useAppSelector(
        (state) => getEntitledAssetsByTransactionType(state, currentWallet.FirmId)
    );
    const [ confirm, setConfirm ] = React.useState<boolean>(false);

    const assetOptions: SelectOption[] = (entitledAssetsByType[AllowedTransactionType.Collateral] ?? [])
        .map(asset => ({
            label: asset.Ticker ?? '',
            value: asset.Ticker ?? '',
            refData: asset.Type === AssetTypes.ASSET ?
                allSecurityCurrencyPairs.find(pair => pair.security?.Ticker === asset.Ticker)
                : { currency: asset }
        }));

    const walletDetailHeader = React.useMemo(() => {
        return (
            <WalletCompactDetails
                accountName={currentWallet.AccountName}
                accountNo={currentWallet?.linkedBrokerDealerAccount ? currentWallet.linkedBrokerDealerAccount : ''}
            />
        );
    }, [
        currentWallet.AccountName,
        currentWallet.Account
    ]);

    React.useEffect(() => {
        setTransactionValues({
            ...transactionValues,
            Sender: currentWallet
        });
    }, [currentWallet]);

    const confirmCreatePledge = (values) => {
        setTransactionValues({
            ...values,
            Sender: currentWallet
        });
        setConfirm(true);
    };

    return (
        <ActionOverlay
            admin={false}
            goBack={goBack}
            headerDetailsComponent={walletDetailHeader}
            title={title}
            type={PaymentActionTypes['createPledge'].type}
        >
            {confirm ? (
                <CreatePledgeConfirmation
                    cancelConfirmation={() => {
                        if (transactionValues && transactionValues.FiatAmount && typeof transactionValues.FiatAmount === 'number') {
                            setTransactionValues({
                                ...transactionValues,
                                FiatAmount: `${transactionValues.FiatAmount}`
                            });
                        }

                        if (transactionValues && transactionValues.AssetAmount && typeof transactionValues.AssetAmount === 'number') {
                            setTransactionValues({
                                ...transactionValues,
                                AssetAmount: `${transactionValues.AssetAmount}`
                            });
                        }
                        setConfirm(false);
                    }}
                    createPledgeValues={transactionValues}
                    currentWallet={currentWallet}
                    handleCreatePledge={handleCreatePledge}
                />
            ) : (
                <CreatePledgeForm
                    formProps={{
                        addressBookOptions: addressBookOptions,
                        currentWallet: currentWallet,
                        accountPositionBalance: accountPositionBalance,
                        getTransactionsSync: getTransactionsSync,
                        handleCreatePledge: confirmCreatePledge,
                        assetOptions,
                        getAccount,
                        validateWalletAddressWithCore
                    }}
                />
            )}
        </ActionOverlay>
    );
}